import { CSSProperties, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { INACTIVE_MOBILE_RESULTS, type MobileResultsValue } from "./context"
import { acquireHostScrollLock, neutralizeFixedTraps } from "./hostPage"
import {
  clampDragTranslate,
  computeFramePadding,
  computeSnaps,
  frameSnapFor,
  parseTranslateY,
  settleSnap,
  type FramePadding,
  type MobileResultsSnap,
} from "./sheetPhysics"

/**
 * Mobile results sheet — the state machine (#856), a port of the toolkit's
 * explorer-mobile-sheet v2 (homerunner-toolkit assets/js/explorer-mobile-sheet.js,
 * line refs inline). ExplorerContent owns the DOM and calls this hook in
 * BOTH layouts (every effect is inert while `active` is false) so the results
 * grid keeps ONE React position across the switch: the `.hfse-body` element
 * simply becomes the sheet (`sheetRef`), and the grid inside it is never
 * remounted — a remount reset PropertyGrid's per-mount quote queue and
 * re-requested every full-price quote on each phone load (codex fresh pass).
 *
 * The module bolts a shell onto the WP plugin's DOM: it MOVES the card
 * wrapper into a sheet it appends, proxies the plugin's filter form as a
 * full-screen panel, and hooks Google's Map prototype to reshape the
 * plugin's fitBounds. Here the same shape is composition —
 *
 *   sidebar   → fixed full-bleed backdrop (CSS)
 *   topbar    → fixed search CTA under `navOffsetPx` (module topbar +
 *               `below_theme_nav`/`nav_offset`; js:351-460)
 *   filters   → legacy bar: a full-screen panel the CTA reveals (module
 *               `.hfse-body` proxy, js:577-603); Searchbar Pro: its own
 *               mobile trigger IS the CTA (module relocates SBP's trigger
 *               into the topbar, js:424-459) and opens the SBP search sheet
 *   sheet     → the body element, fixed, draggable, three snaps (js:1278-1400)
 *   fab       → floating Map/List pill (js:551-560)
 *
 * The map renderers read the sheet geometry through MobileResultsContext so
 * their programmatic fits land in the visible band (module bandPadding,
 * js:686-707) — no prototype hooks needed.
 *
 * Not ported (see the PR body): the module's theme-header hiding and
 * favicon/hamburger bar (theme-specific selectors; `navOffsetPx` is the
 * supported way to sit under a host nav), the viewport-meta zoom lock (the
 * 16px control rule stays), the Google band-camera/minZoom relaxation, the
 * mobile-dock edge trigger (no dock module in the widget) and the debug HUD.
 */

export interface MobileResultsSettings {
  navOffsetPx: number
  initialSnap: Exclude<MobileResultsSnap, 'full'>
  /** Resolved by ExplorerContent and surfaced back on the sheet object, so the
   *  FAB gate and this setting can't drift apart. */
  showToggle: boolean
}

export interface MobileResultsSheetParams {
  active: boolean
  containerRef: RefObject<HTMLDivElement | null>
  settings: MobileResultsSettings
  count: { total: number; hasData: boolean }
}

function visualViewportBox(): { left: number; top: number; width: number; height: number } {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  return {
    left: vv?.offsetLeft ?? 0,
    top: vv?.offsetTop ?? 0,
    width: vv?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 0),
    height: vv?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 0),
  }
}

/** Search-button selectors whose tap closes the legacy filter panel (js:1492-1508). */
const SEARCH_SUBMIT_SELECTOR = '.hfse-form-search-btn, .ai_search_submit, .relevance_search_submit'
/** A pointer that travels further than this before release is a drag, not a tap. */
const TAP_SLOP_PX = 4
/** A release this long after the last movement is a hold, not a flick. */
const FLICK_STALE_MS = 100
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function useMobileResultsSheet({ active, containerRef, settings, count }: MobileResultsSheetParams) {
  /** The `.hfse-body` element — the sheet AND its scroll region while active. */
  const sheetRef = useRef<HTMLDivElement>(null)
  const topbarRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLButtonElement>(null)
  // The live snap for listeners that must not close over render state.
  const snapRef = useRef<MobileResultsSnap>(settings.initialSnap)
  const [snap, setSnap] = useState<MobileResultsSnap>(settings.initialSnap)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [noMotion, setNoMotion] = useState(false)
  // Set by a drag that actually moved; the click-capture guard and the
  // handle's click handler consult it so a drag never doubles as a tap.
  const movedRef = useRef(false)
  const draggingRef = useRef(false)

  /**
   * js:1291-1311 `applySnap` — position the sheet at a snap. Snaps are
   * relative to the VISUAL viewport (offsetTop + fraction of its height),
   * so with the iOS keyboard or URL bar shifting the visual viewport the
   * sheet's handle stays on screen instead of following the layout viewport
   * off the top. Written straight to the element (transform is hot-path
   * during drags); React state mirrors it for classes.
   */
  const applySnap = useCallback((name: MobileResultsSnap, animate: boolean) => {
    const sheet = sheetRef.current
    if (!sheet) return
    const vv = visualViewportBox()
    const snaps = computeSnaps(vv.height)
    snapRef.current = name
    sheet.style.transition = animate ? '' : 'none'
    sheet.style.transform = `translateY(${vv.top + snaps[name]}px)`
    setSnap(name)
  }, [])

  // Leaving the layout: drop the inline transform/transition the sheet wrote
  // on the body element, and close the panel.
  useEffect(() => {
    if (active) return
    const sheet = sheetRef.current
    if (sheet) {
      sheet.style.transform = ''
      sheet.style.transition = ''
    }
    setFiltersOpen(false)
  }, [active])

  // Host page: scroll lock + transformed-ancestor neutralization for the
  // life of the layout (module activate/deactivate, js:1402-1412 / 1584-);
  // reference-counted in hostPage.ts so an owner handover cannot strand them.
  useEffect(() => {
    if (!active) return
    const unlock = acquireHostScrollLock()
    const restore = neutralizeFixedTraps(containerRef.current)
    return () => {
      restore()
      unlock()
    }
  }, [active, containerRef])

  // Initial rest position (js:1531-1533: rAF → applySnap).
  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    const id = window.requestAnimationFrame(() => applySnap(settings.initialSnap, true))
    return () => window.cancelAnimationFrame(id)
  }, [active, applySnap, settings.initialSnap])

  // js:1572-1582 `onViewport`: keep the sheet positioned when the visible
  // viewport changes — keyboard, iOS URL bar — and publish the visible box
  // as CSS variables (initialized immediately) so the fixed filter panel and
  // the sheet size themselves to the area the visitor can actually see (an
  // overlay keyboard does not shrink the layout viewport). Deliberately NO
  // map re-fit here: the module learned that re-fitting on every
  // visualViewport resize re-panned the map continuously (iOS fires it on
  // scroll / URL-bar show-hide). Skipped mid-drag: re-snapping there would
  // overwrite the pointer position and restore the transition under a live
  // gesture (codex fresh pass).
  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    const vv = window.visualViewport
    const publish = () => {
      const box = visualViewportBox()
      const container = containerRef.current
      if (container) {
        container.style.setProperty('--hfse-mres-vv-top', `${box.top}px`)
        container.style.setProperty('--hfse-mres-vv-height', `${box.height}px`)
      }
      const topbar = topbarRef.current
      if (topbar) topbar.style.transform = `translateY(${box.top}px)`
    }
    const onViewport = () => {
      // `animate: false`. visualViewport `scroll` fires continuously through a
      // pinch-pan or a URL-bar collapse; animating restarts the 0.34s
      // transition every event, so the sheet never arrives and rubber-bands
      // behind the viewport for the whole gesture.
      if (!draggingRef.current) applySnap(snapRef.current, false)
      publish()
    }
    publish()
    vv?.addEventListener('resize', onViewport)
    vv?.addEventListener('scroll', onViewport)
    window.addEventListener('resize', onViewport, { passive: true })
    return () => {
      vv?.removeEventListener('resize', onViewport)
      vv?.removeEventListener('scroll', onViewport)
      window.removeEventListener('resize', onViewport)
      const container = containerRef.current
      container?.style.removeProperty('--hfse-mres-vv-top')
      container?.style.removeProperty('--hfse-mres-vv-height')
    }
  }, [active, applySnap, containerRef])

  // Reduced motion via a matchMedia read rather than a new @media block:
  // explorer.css's motion contract (searchbar-motion-css test) reads the
  // LAST prefers-reduced-motion block in the file, which must stay the
  // searchbar kill list.
  useEffect(() => {
    if (!active || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setNoMotion(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [active])

  // js:1318-1400 `bindDrag` — native listeners: React marks touchmove
  // passive at the root, and the sheet must preventDefault mid-drag so the
  // page/list does not scroll under the finger.
  useEffect(() => {
    const sheet = sheetRef.current
    if (!active || !sheet || typeof window === 'undefined') return

    let startY = 0
    let startT = 0
    let curT = 0
    let lastY = 0
    let lastTime = 0
    let velocity = 0 // px per ms, positive = downward

    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const pointY = (e: TouchEvent | MouseEvent): number =>
      'touches' in e ? (e.touches[0]?.clientY ?? lastY) : e.clientY

    const endDrag = () => {
      draggingRef.current = false
      sheet.style.transition = ''
    }

    const down = (e: TouchEvent | MouseEvent) => {
      // A new gesture always starts clean: a moved touch drag (or a blur
      // release) produces no click, so a stale flag would swallow the next
      // legitimate card tap (codex r4).
      movedRef.current = false
      const y = pointY(e)
      const atTop = sheet.scrollTop <= 0
      const target = e.target as Element | null
      // The head is a drag surface, but it now carries the SecondaryRow — a
      // native <select> sort control. Grabbing that started a sheet drag: the
      // sheet jumped, the non-passive touchmove preventDefault'd the picker
      // open, and onClickCapture swallowed the release. Interactive descendants
      // keep their own gestures.
      const onInteractive = !!target?.closest?.('select, input, textarea, button, a, [role="button"], [role="combobox"], [role="listbox"]')
      const onGrabSurface = !!target?.closest?.('.hfse-mres-grab, .hfse-mres-head')
      // `.hfse-mres-grab` is itself a <button> and IS the handle — it must keep
      // starting drags even though it matches the interactive test.
      const onGrab = onGrabSurface && (!onInteractive || !!target?.closest?.('.hfse-mres-grab'))
      if (!onGrab && !atTop) return // let the list scroll natively
      draggingRef.current = true
      startY = y
      startT = parseTranslateY(sheet.style.transform) ?? (visualViewportBox().top + computeSnaps(visualViewportBox().height).half)
      curT = startT
      lastY = y
      lastTime = now()
      velocity = 0
      sheet.style.transition = 'none'
    }

    const move = (e: TouchEvent | MouseEvent) => {
      if (!draggingRef.current) return
      // A mouse released outside the window never delivers `mouseup`; the
      // next movement arrives with no button held — settle instead of
      // dragging an unsnapped sheet forever (codex r1).
      if (!('touches' in e) && e.buttons === 0) { up(); return }
      const y = pointY(e)
      const dy = y - startY
      // Fully expanded and dragging UP from rest: that is the list scrolling,
      // not the sheet. Only with zero displacement — once the sheet has been
      // pulled down, a reversal above the start must stay a sheet drag (the
      // clamp holds it at `full`) so the release settles it (codex r2/r3).
      if (snapRef.current === 'full' && dy < 0 && curT === startT) { endDrag(); return }
      if (Math.abs(dy) > TAP_SLOP_PX) movedRef.current = true
      const vv = visualViewportBox()
      const snaps = computeSnaps(vv.height)
      curT = vv.top + clampDragTranslate(startT - vv.top + dy, snaps)
      sheet.style.transform = `translateY(${curT}px)`
      const t = now()
      const dt = t - lastTime
      if (dt > 0) velocity = 0.6 * velocity + 0.4 * ((y - lastY) / dt)
      lastY = y
      lastTime = t
      if (e.cancelable) e.preventDefault()
    }

    const up = () => {
      if (!draggingRef.current) return
      endDrag()
      const vv = visualViewportBox()
      const snaps = computeSnaps(vv.height)
      // A hold after the last movement is not a flick (codex fresh pass).
      const v = now() - lastTime > FLICK_STALE_MS ? 0 : velocity
      applySnap(settleSnap(curT - vv.top, snaps, v), true)
    }

    // A drag that started on a card link must not also click it when the
    // pointer is released over it (codex fresh pass): swallow the click the
    // browser synthesizes after a moved mouse drag. Touch drags preventDefault
    // their moves, so no click follows them.
    const onClickCapture = (e: MouseEvent) => {
      if (!movedRef.current) return
      movedRef.current = false
      e.preventDefault()
      e.stopPropagation()
    }

    sheet.addEventListener('touchstart', down, { passive: true })
    sheet.addEventListener('touchmove', move, { passive: false })
    sheet.addEventListener('touchend', up)
    sheet.addEventListener('touchcancel', up)
    sheet.addEventListener('mousedown', down)
    sheet.addEventListener('click', onClickCapture, true)
    // Window-level so a mouse drag continues when the pointer leaves the sheet;
    // blur settles a drag the window lost (tab switch, release outside).
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    window.addEventListener('blur', up)
    return () => {
      sheet.removeEventListener('touchstart', down)
      sheet.removeEventListener('touchmove', move)
      sheet.removeEventListener('touchend', up)
      sheet.removeEventListener('touchcancel', up)
      sheet.removeEventListener('mousedown', down)
      sheet.removeEventListener('click', onClickCapture, true)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('blur', up)
      // Torn down mid-drag: never leave the host with a half-dragged sheet state.
      draggingRef.current = false
    }
  }, [active, applySnap])

  // Legacy filter panel: it declares itself a modal dialog, so behave like
  // one — move focus in on open, keep Tab inside, Escape closes, restore
  // focus to the CTA on close (codex fresh pass).
  useEffect(() => {
    if (!filtersOpen || typeof document === 'undefined') return
    const panel = panelRef.current
    // In production the widget lives in a shadow root: `document.activeElement`
    // is the HOST and `document.contains(child)` is false, so read focus from
    // the panel's own root and check connectivity (codex r4).
    const root = (panel?.getRootNode() ?? document) as Document | ShadowRoot
    const activeIn = () => root.activeElement
    const previous = activeIn() as HTMLElement | null
    const first = panel?.querySelector<HTMLElement>('.hfse-mres-filter-close')
    first?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFiltersOpen(false); return }
      if (e.key !== 'Tab' || !panel) return
      const current = activeIn()
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === current)
      if (focusables.length === 0) return
      const firstEl = focusables[0]
      const lastEl = focusables[focusables.length - 1]
      if (e.shiftKey && (current === firstEl || !panel.contains(current))) { e.preventDefault(); lastEl.focus() }
      else if (!e.shiftKey && (current === lastEl || !panel.contains(current))) { e.preventDefault(); firstEl.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      const restoreTo = ctaRef.current ?? previous
      if (restoreTo && typeof restoreTo.focus === 'function' && restoreTo.isConnected) restoreTo.focus()
    }
  }, [filtersOpen])

  const openFilters = useCallback(() => setFiltersOpen(true), [])
  const closeFilters = useCallback(() => setFiltersOpen(false), [])
  // js:1492-1508: a search run from the panel closes it (submit, or a tap on
  // any search button). Capture phase: React batches, so the field's own
  // handlers still run in this same dispatch.
  const onFiltersClickCapture = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element | null
    if (target?.closest?.(SEARCH_SUBMIT_SELECTOR)) setFiltersOpen(false)
  }, [])

  // js:551-560: the pill flips list ↔ map; the handle tap does the same.
  const toggleListMode = useCallback(() => {
    applySnap(snapRef.current === 'full' ? 'half' : 'full', true)
  }, [applySnap])
  const onGrabClick = useCallback(() => {
    if (movedRef.current) { movedRef.current = false; return }
    toggleListMode()
  }, [toggleListMode])

  // js:686-707 `bandPadding` — read live by the renderers' fitBounds.
  const getFramePadding = useCallback((mapEl: HTMLElement | null): FramePadding | null => {
    if (!mapEl || typeof window === 'undefined') return null
    const r = mapEl.getBoundingClientRect()
    if (!(r.width > 0) || !(r.height > 0)) return null
    const viewport = visualViewportBox()
    const snaps = computeSnaps(viewport.height)
    const sheetTop = viewport.top + snaps[frameSnapFor(snapRef.current)]
    const tb = topbarRef.current?.getBoundingClientRect()
    const topbarBottom = tb && tb.height > 0 ? tb.bottom : null
    return computeFramePadding({ mapRect: r, viewport, sheetTop, topbarBottom })
  }, [])

  // Identity-stable for the life of the layout (see context.ts): a per-snap
  // value would re-render the map renderers and rebuild their markers.
  const ctx = useMemo<MobileResultsValue>(
    () => (active ? { active: true, getFramePadding } : INACTIVE_MOBILE_RESULTS),
    [active, getFramePadding],
  )

  // js:1235-1276 `updateCount`: never flash "No stays found" mid-load.
  const countLabel = !count.hasData
    ? 'Loading…'
    : count.total > 0
      ? `${count.total} ${count.total === 1 ? 'stay' : 'stays'}`
      : 'No stays found'
  const listMode = snap === 'full'

  const rootClass = active
    ? ['hfse-mres', `hfse-mres-snap-${snap}`, filtersOpen ? 'hfse-mres-filters-open' : '', noMotion ? 'hfse-mres-no-motion' : ''].filter(Boolean).join(' ')
    : ''
  const rootStyle = useMemo<CSSProperties>(
    () => (active ? ({ '--hfse-mres-nav-offset': `${settings.navOffsetPx}px` } as CSSProperties) : {}),
    [active, settings.navOffsetPx],
  )
  const sheetClass = active ? `hfse-mres-sheet hfse-mres-scroll hfse-mres-sheet-init-${settings.initialSnap}` : ''

  return {
    active, snap, listMode, filtersOpen, countLabel, ctx,
    sheetRef, topbarRef, panelRef, ctaRef,
    rootClass, rootStyle, sheetClass,
    // Surfaced so the FAB gate reads the same resolved value the hook was
    // handed, instead of re-deriving `?? true` at the render site.
    showToggle: settings.showToggle,
    openFilters, closeFilters, onFiltersClickCapture, toggleListMode, onGrabClick,
  }
}

export type MobileResultsSheet = ReturnType<typeof useMobileResultsSheet>
